/**
 * Box Truck Drivers — cross-tenant staff view. Every box-truck driver on the platform, their full
 * registration (the same composed view the owning operator sees in the admin app), and WHICH COMPANY
 * (application) claimed them, plus platform-wide stats and a per-company breakdown. Read-only.
 * Backend: GET /v1/gluon/box-truck-drivers (any control-panel operator).
 */
import * as React from 'react'
import axios from 'axios'
import {
    BOX_TRUCK_DOC_LABELS,
    BOX_TRUCK_DOC_TYPES,
    boxTruckVerificationDocs,
    formatBoxTruckDimensions,
    formatBoxTruckDoor,
    type BoxTruckVerificationPath,
} from '@numeo/types-core'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import ChipDelete from '@mui/joy/ChipDelete'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Tabs from '@mui/joy/Tabs'
import Table from '@mui/joy/Table'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import BusinessIcon from '@mui/icons-material/Business'
import GroupsIcon from '@mui/icons-material/Groups'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { fmtDate, StatCard } from '../_shared/StatCard'

interface DriverInfo {
    name?: string
    phone?: string
    equipmentType?: string
    truckNumber?: string
    companyPhone?: string
    emergencyContact?: string
    emergencyContactPhone?: string
    dotNumber?: string
    mcNumber?: string
    bankName?: string
    bankAccountNumber?: string
    bankRoutingNumber?: string
    cargoDimensions?: string
    vehicleMake?: string
    vehicleModel?: string
    vehicleYear?: string
    vinNumber?: string
    doorWidth?: string
    doorHeight?: string
    insuranceExpirationDate?: string
    licenseNumber?: string
    licenseState?: string
    licenseType?: string
    licenseExpirationDate?: string
    plannedAddress?: string
    maxCargoWeightLbs?: number
    documents?: { type: string; fileName: string; gcsPath: string; contentType?: string; uploadedAt?: string; removedAt?: string; removedBy?: string }[]
    boxTruckOwnerInfo?: DriverBoxTruckOwnerInfo
    agreedToTermsAt?: string
}

/** The driver's carrier/company OWNER info (mirrors @numeo/types BoxTruckOwnerInfo; ssn intentionally omitted —
 *  PII, never shown in the staff UI). Distinct from `DriverOwner`, which is the numeo application that claimed
 *  the driver. */
interface DriverBoxTruckOwnerInfo {
    companyName?: string
    applicantFirstName?: string
    applicantLastName?: string
    email?: string
    companyPhone?: string
    applicantPhone?: string
    emergencyPhone?: string
    employerId?: string
    address?: string
    city?: string
    state?: string
    zip?: string
    founded?: string
    title?: string
    companyType?: string
}

/** Who claimed the driver into My Drivers — the HR manager. */
interface DriverHrManager {
    userId?: string
    email?: string
    name?: string
    assignedAt?: string
}

interface DriverOwner {
    applicationId: string
    projectName?: string
    companyName?: string
    ownerEmail?: string
    claimedAt?: string
}

interface DriverRow {
    userId: string
    email: string
    name: string
    /** Sign-up phone (root account phone for phone-OTP sign-ups) — contact fallback when the request has none. */
    signupPhone?: string
    /** The driver signed up by PHONE: `email` is a synthetic …@phone.numeo.invalid login, lead with the phone. */
    phoneSignup?: boolean
    /** Gluon-only staff note (never visible on tenant/admin surfaces). */
    staffNote?: { note: string; updatedBy: string | null; updatedAt: string }
    status?: 'pending' | 'active' | 'inactive'
    enabled: boolean
    verificationPath?: string
    submittedAt?: string
    reviewedAt?: string
    info?: DriverInfo
    profileLocation?: { city?: string; state?: string }
    lastLocation?: { lat: number; lng: number; city?: string; state?: string; updatedAt?: string }
    owner?: DriverOwner
    hrManager?: DriverHrManager
}

interface CompanyRow {
    applicationId: string
    projectName?: string
    companyName?: string
    ownerEmail?: string
    driverCount: number
}

interface Stats {
    total: number
    claimed: number
    unclaimed: number
    byStatus: Record<string, number>
    mcHolders: number
    companies: CompanyRow[]
}

const PAGE_SIZE = 50

/** Section labels per document type — the shared canonical set, same order/wording as the admin card. */
const DOC_TYPE_LABELS: Record<string, string> = BOX_TRUCK_DOC_LABELS

/** Which document sections to render for a driver: their self-verification path's slots plus anything
 *  actually on the record. Without the path filter every driver gets all 10 kinds and a compliant
 *  mc_holder (2 slots) shows 8 amber "Not uploaded" rows. Operator-registered drivers (no path) keep the
 *  full operator set. */
const docSectionTypes = (path: string | undefined, docs: { type: string }[]): readonly string[] => {
    if (!path) return BOX_TRUCK_DOC_TYPES
    const wanted = new Set<string>(boxTruckVerificationDocs(path as BoxTruckVerificationPath).map((s) => s.type))
    for (const d of docs) wanted.add(d.type)
    return BOX_TRUCK_DOC_TYPES.filter((t) => wanted.has(t))
}

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'neutral'> = { active: 'success', pending: 'warning', inactive: 'neutral' }

const companyLabel = (owner?: DriverOwner | CompanyRow): string => owner?.companyName || owner?.projectName || '—'

/** One stats tile in the header row. */
/** A labelled value inside the detail modal. */
function DetailField({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10 }}>
                {label}
            </Typography>
            <Typography level="body-sm" sx={{ wordBreak: 'break-word' }}>
                {value || '—'}
            </Typography>
        </Box>
    )
}

export default function BoxTruckDriversPage() {
    const [rows, setRows] = React.useState<DriverRow[]>([])
    const [stats, setStats] = React.useState<Stats | null>(null)
    const [total, setTotal] = React.useState(0)
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)
    const [searchInput, setSearchInput] = React.useState('')
    const [search, setSearch] = React.useState('')
    const [status, setStatus] = React.useState<string>('')
    const [detail, setDetail] = React.useState<DriverRow | null>(null)
    // The audience split, same as the tenant queues: Drivers = no-MC document path (+ legacy),
    // Company Drivers = MC-verified (mc_holder path).
    const [tab, setTab] = React.useState<'drivers' | 'company_owners'>('drivers')
    const [companiesOpen, setCompaniesOpen] = React.useState(false)
    // Company drill-down: set from the breakdown modal (or the table's Company cell) — the table then shows
    // ONLY that company's drivers until the chip is cleared.
    const [companyFilter, setCompanyFilter] = React.useState<{ applicationId: string; label: string } | null>(null)
    const [docLoading, setDocLoading] = React.useState<string | null>(null)
    // Blob URLs handed to new tabs — revoked together on unmount (revoking right after window.open would
    // race the new tab's load and blank it).
    const viewedUrls = React.useRef<string[]>([])
    React.useEffect(
        () => () => {
            viewedUrls.current.forEach((url) => URL.revokeObjectURL(url))
            viewedUrls.current = []
        },
        []
    )

    // Open a driver's document in a NEW TAB: the tab is opened synchronously (inside the click) so popup
    // blockers allow it, then pointed at the authed blob once fetched (bytes are proxied through
    // main-service — no signed URL).
    const viewDocument = React.useCallback(async (userId: string, doc: { gcsPath: string; fileName: string }) => {
        const win = window.open('', '_blank')
        setDocLoading(doc.gcsPath)
        try {
            const res = await axios.get<Blob>(`/gluon/box-truck-drivers/${userId}/documents/file`, {
                params: { gcsPath: doc.gcsPath },
                responseType: 'blob',
            })
            const url = URL.createObjectURL(res.data)
            viewedUrls.current.push(url)
            if (win) win.location.href = url
            else window.open(url, '_blank')
        } catch (e) {
            console.error('[box-truck-drivers] document view failed', e)
            win?.close()
            setError(`Couldn’t open ${doc.fileName}`)
        } finally {
            setDocLoading(null)
        }
    }, [])

    // Gluon-only staff note editor (detail modal). Draft is seeded when a driver's modal opens; saving
    // upserts server-side (empty → cleared) and patches the row + open modal in place.
    const [noteDraft, setNoteDraft] = React.useState('')
    const [noteSaving, setNoteSaving] = React.useState(false)
    const openDetail = React.useCallback((row: DriverRow) => {
        setNoteDraft(row.staffNote?.note ?? '')
        setDetail(row)
    }, [])
    const saveNote = React.useCallback(async () => {
        if (!detail) return
        setNoteSaving(true)
        try {
            const res = await axios.put<{ success: boolean; data: { staffNote: DriverRow['staffNote'] | null } }>(
                `/gluon/box-truck-drivers/${detail.userId}/note`,
                { note: noteDraft }
            )
            const staffNote = res.data.data.staffNote ?? undefined
            setDetail((prev) => (prev && prev.userId === detail.userId ? { ...prev, staffNote } : prev))
            setRows((prev) => prev.map((r) => (r.userId === detail.userId ? { ...r, staffNote } : r)))
        } catch (e) {
            console.error('[box-truck-drivers] note save failed', e)
            setError('Couldn’t save the note')
        } finally {
            setNoteSaving(false)
        }
    }, [detail, noteDraft])

    // Staff status override (incl. back to 'pending' — the tenant operator UI can't do that). Patches the
    // row + open modal in place; the header stats refresh on the next load.
    const [statusBusy, setStatusBusy] = React.useState<string | null>(null)
    const changeStatus = React.useCallback(async (userId: string, status: 'pending' | 'active' | 'inactive') => {
        setStatusBusy(userId)
        try {
            const res = await axios.patch<{ success: boolean; data: { status: DriverRow['status']; enabled: boolean } }>(
                `/gluon/box-truck-drivers/${userId}/status`,
                { status }
            )
            const next = res.data.data
            setRows((prev) => prev.map((r) => (r.userId === userId ? { ...r, status: next.status, enabled: next.enabled } : r)))
            setDetail((prev) => (prev && prev.userId === userId ? { ...prev, status: next.status, enabled: next.enabled } : prev))
        } catch (e) {
            console.error('[box-truck-drivers] status change failed', e)
            setError('Couldn’t update the driver status')
        } finally {
            setStatusBusy(null)
        }
    }, [])

    // Debounce the search box → committed `search` drives the fetch.
    React.useEffect(() => {
        const t = window.setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => window.clearTimeout(t)
    }, [searchInput])

    const load = React.useCallback(
        async (targetPage: number, searchTerm: string, statusFilter: string, applicationId?: string, audience?: 'drivers' | 'company_owners') => {
            setLoading(true)
            setError(null)
            try {
                const res = await axios.get<{ success: boolean; data: { items: DriverRow[]; total: number; stats: Stats } }>('/gluon/box-truck-drivers', {
                    params: {
                        page: targetPage,
                        limit: PAGE_SIZE,
                        ...(searchTerm ? { search: searchTerm } : {}),
                        ...(statusFilter ? { status: statusFilter } : {}),
                        ...(applicationId ? { applicationId } : {}),
                        ...(audience ? { audience } : {}),
                    },
                })
                setRows(res.data.data.items)
                setTotal(res.data.data.total)
                setStats(res.data.data.stats)
                setPage(targetPage)
            } catch (e) {
                console.error('[box-truck-drivers] list load failed', e)
                setError(e instanceof Error ? e.message : 'Failed to load box truck drivers')
            } finally {
                setLoading(false)
            }
        },
        []
    )

    React.useEffect(() => {
        void load(1, search, status, companyFilter?.applicationId, tab)
    }, [load, search, status, companyFilter, tab])

    // Pick a company (breakdown modal row or a table Company cell) → the current tab's table becomes that
    // company's roster.
    const drillIntoCompany = React.useCallback((company: { applicationId: string } & Partial<CompanyRow> & Partial<DriverOwner>) => {
        setCompanyFilter({ applicationId: company.applicationId, label: companyLabel(company as CompanyRow) })
        setCompaniesOpen(false)
    }, [])

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography level="h3" startDecorator={<LocalShippingIcon />}>
                        Box Truck Drivers
                    </Typography>
                    <Typography level="body-sm" textColor="text.tertiary">
                        Every box-truck driver on the platform, their registration and which company they belong to
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="sm"
                    startDecorator={<RefreshIcon />}
                    onClick={() => void load(page, search, status, companyFilter?.applicationId, tab)}
                    loading={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Stats */}
            {stats && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <StatCard icon={<GroupsIcon />} label="Total drivers" value={stats.total} />
                    <StatCard icon={<BusinessIcon />} label="Claimed by a company" value={stats.claimed} hint={`${stats.unclaimed} in the open pool`} />
                    <Sheet
                        variant="outlined"
                        onClick={() => setCompaniesOpen(true)}
                        sx={{
                            borderRadius: 'lg',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minWidth: 150,
                            flex: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'background.level1' },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 'md',
                                bgcolor: 'primary.softBg',
                                color: 'primary.500',
                                width: 40,
                                height: 40,
                            }}
                        >
                            <BusinessIcon />
                        </Box>
                        <Box>
                            <Typography level="h4" sx={{ lineHeight: 1.1 }}>
                                {stats.companies.length}
                            </Typography>
                            <Typography level="body-xs" textColor="text.tertiary">
                                Companies with drivers · view breakdown
                            </Typography>
                        </Box>
                    </Sheet>
                    <StatCard icon={<VerifiedUserIcon />} label="MC holders (Company Owners)" value={stats.mcHolders} />
                    <StatCard
                        icon={<PersonSearchIcon />}
                        label="By status"
                        value={
                            <Box component="span" sx={{ display: 'inline-flex', gap: 0.75 }}>
                                <Chip size="sm" color="success" variant="soft">
                                    {stats.byStatus.active ?? 0} active
                                </Chip>
                                <Chip size="sm" color="warning" variant="soft">
                                    {stats.byStatus.pending ?? 0} pending
                                </Chip>
                                <Chip size="sm" color="neutral" variant="soft">
                                    {stats.byStatus.inactive ?? 0} inactive
                                </Chip>
                            </Box>
                        }
                    />
                </Box>
            )}

            {/* Audience tabs — same split as the tenant queues: Drivers = requested WITHOUT an MC (document
                path + legacy); Company Drivers = verified via their MC (mc_holder path). */}
            <Tabs value={tab} onChange={(_, v) => setTab(v as 'drivers' | 'company_owners')} sx={{ bgcolor: 'transparent' }}>
                <TabList size="sm" sx={{ width: 'fit-content' }}>
                    <Tab value="drivers">Drivers{stats ? ` (${Math.max(0, stats.total - stats.mcHolders)})` : ''}</Tab>
                    <Tab value="company_owners">Company Drivers{stats ? ` (${stats.mcHolders})` : ''}</Tab>
                </TabList>
            </Tabs>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}

            {/* Filters (apply within the active tab) */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Input
                    startDecorator={<SearchIcon />}
                    placeholder="Search name, email or phone…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{ width: 320 }}
                    size="sm"
                />
                <Select size="sm" value={status} onChange={(_, v) => setStatus(v ?? '')} sx={{ width: 160 }} placeholder="All statuses">
                    <Option value="">All statuses</Option>
                    <Option value="active">Active</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="inactive">Inactive</Option>
                </Select>
                {companyFilter && (
                    <Chip size="sm" variant="soft" color="primary" endDecorator={<ChipDelete onDelete={() => setCompanyFilter(null)} />}>
                        Company: {companyFilter.label}
                    </Chip>
                )}
                <Typography level="body-xs" textColor="text.tertiary" sx={{ ml: 'auto' }}>
                    {total} driver{total === 1 ? '' : 's'} match{total === 1 ? 'es' : ''}
                </Typography>
            </Box>

            {/* Drivers table (the active tab's audience) */}
            <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
                <Table hoverRow stickyHeader sx={{ '--TableCell-paddingX': '12px', minWidth: 980 }}>
                    <thead>
                        <tr>
                            <th style={{ width: 220 }}>Driver</th>
                            <th style={{ width: 130 }}>Phone</th>
                            <th style={{ width: 130 }}>Equipment</th>
                            <th style={{ width: 90 }}>Status</th>
                            <th style={{ width: 110 }}>Path</th>
                            <th style={{ width: 150 }}>Location</th>
                            <th style={{ width: 220 }}>Company</th>
                            <th style={{ width: 150 }}>Submitted</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && rows.length === 0 ? (
                            <tr>
                                <td colSpan={8}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={8}>
                                    <Typography level="body-sm" textColor="text.tertiary" sx={{ textAlign: 'center', py: 6 }}>
                                        No drivers match.
                                    </Typography>
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.userId} onClick={() => openDetail(row)} style={{ cursor: 'pointer' }}>
                                    <td>
                                        <Typography level="body-sm" fontWeight="lg">
                                            {row.name || '—'}
                                        </Typography>
                                        <Typography level="body-xs" textColor="text.tertiary" noWrap>
                                            {row.phoneSignup ? `${row.signupPhone || row.email.split('@')[0]} · phone sign-up` : row.email}
                                        </Typography>
                                        {row.staffNote && (
                                            <Typography level="body-xs" color="warning" noWrap sx={{ fontStyle: 'italic', maxWidth: 220 }}>
                                                📝 {row.staffNote.note}
                                            </Typography>
                                        )}
                                    </td>
                                    <td>
                                        {row.info?.phone || row.signupPhone || '—'}
                                        {!row.info?.phone && row.signupPhone && (
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                from sign-up
                                            </Typography>
                                        )}
                                    </td>
                                    <td>
                                        {row.info?.equipmentType || '—'}
                                        {row.info?.maxCargoWeightLbs != null && (
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {row.info.maxCargoWeightLbs.toLocaleString()} lbs
                                            </Typography>
                                        )}
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <Select
                                            size="sm"
                                            variant="soft"
                                            color={STATUS_COLOR[row.status ?? ''] ?? 'neutral'}
                                            value={row.status ?? ''}
                                            disabled={statusBusy === row.userId}
                                            onChange={(_, v) => {
                                                if (v && v !== row.status) void changeStatus(row.userId, v as 'pending' | 'active' | 'inactive')
                                            }}
                                            sx={{ minWidth: 104 }}
                                        >
                                            <Option value="active">active</Option>
                                            <Option value="pending">pending</Option>
                                            <Option value="inactive">inactive</Option>
                                        </Select>
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="outlined" color={row.verificationPath === 'mc_holder' ? 'primary' : 'neutral'}>
                                            {row.verificationPath === 'mc_holder' ? 'MC holder' : row.verificationPath === 'no_mc' ? 'Documents' : 'Legacy'}
                                        </Chip>
                                    </td>
                                    <td>
                                        {row.lastLocation?.city || row.profileLocation?.city
                                            ? `${row.lastLocation?.city || row.profileLocation?.city}, ${row.lastLocation?.state || row.profileLocation?.state || ''}`
                                            : '—'}
                                    </td>
                                    <td>
                                        {row.owner ? (
                                            // Clicking the company drills the table into that company's roster (not the driver detail).
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    drillIntoCompany(row.owner!)
                                                }}
                                                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                title="Show all drivers of this company"
                                            >
                                                <Typography level="body-sm" fontWeight="lg" noWrap>
                                                    {companyLabel(row.owner)}
                                                </Typography>
                                                <Typography level="body-xs" textColor="text.tertiary" noWrap>
                                                    {row.owner.ownerEmail || row.owner.projectName}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Chip size="sm" variant="soft" color="neutral">
                                                Unassigned
                                            </Chip>
                                        )}
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{fmtDate(row.submittedAt)}</Typography>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </Sheet>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        disabled={page <= 1 || loading}
                        onClick={() => void load(page - 1, search, status, companyFilter?.applicationId, tab)}
                    >
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button
                        size="sm"
                        variant="outlined"
                        disabled={page >= totalPages || loading}
                        onClick={() => void load(page + 1, search, status, companyFilter?.applicationId, tab)}
                    >
                        Next
                    </Button>
                </Box>
            )}

            {/* Driver detail modal */}
            <Modal open={!!detail} onClose={() => setDetail(null)}>
                <ModalDialog sx={{ width: 980, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto' }}>
                    <ModalClose />
                    {detail && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 4 }}>
                                <Typography level="h4">{detail.name || detail.email}</Typography>
                                <Chip size="sm" variant="soft" color={STATUS_COLOR[detail.status ?? ''] ?? 'neutral'}>
                                    {detail.status ?? '—'}
                                </Chip>
                                {detail.verificationPath === 'mc_holder' && (
                                    <Chip size="sm" variant="outlined" color="primary">
                                        MC holder
                                    </Chip>
                                )}
                            </Box>

                            {/* Ownership strip — full width, four fields in a row on wide screens. */}
                            <Sheet variant="soft" sx={{ borderRadius: 'md', p: 1.5, mt: 1 }}>
                                {detail.owner ? (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                                        <DetailField label="Belongs to" value={companyLabel(detail.owner)} />
                                        <DetailField label="Project" value={detail.owner.projectName} />
                                        <DetailField label="Company owner" value={detail.owner.ownerEmail} />
                                        <DetailField label="Claimed at" value={fmtDate(detail.owner.claimedAt)} />
                                    </Box>
                                ) : (
                                    <Typography level="body-sm" textColor="text.tertiary">
                                        Not claimed by any company — in the open pool.
                                    </Typography>
                                )}
                            </Sheet>

                            {/* Owner Info — the driver's carrier/company owner (imported from the operator's
                                external fleet system) + who claimed the driver (HR manager). Distinct from the
                                Ownership strip above, which is the numeo application that owns the claim. */}
                            {(detail.info?.boxTruckOwnerInfo || detail.hrManager) && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography level="title-sm" sx={{ mb: 1 }}>
                                        Owner Info
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                                        <DetailField label="Company" value={detail.info?.boxTruckOwnerInfo?.companyName} />
                                        <DetailField
                                            label="Owner"
                                            value={
                                                [detail.info?.boxTruckOwnerInfo?.applicantFirstName, detail.info?.boxTruckOwnerInfo?.applicantLastName]
                                                    .filter(Boolean)
                                                    .join(' ') || undefined
                                            }
                                        />
                                        <DetailField label="Founded" value={detail.info?.boxTruckOwnerInfo?.founded} />
                                        <DetailField label="Employer ID" value={detail.info?.boxTruckOwnerInfo?.employerId} />
                                        <DetailField label="HR Manager" value={detail.hrManager?.name} />
                                        <DetailField label="Title" value={detail.info?.boxTruckOwnerInfo?.title} />
                                        <DetailField label="Company type" value={detail.info?.boxTruckOwnerInfo?.companyType} />
                                        <DetailField label="Owner email" value={detail.info?.boxTruckOwnerInfo?.email} />
                                        <DetailField label="Company phone" value={detail.info?.boxTruckOwnerInfo?.companyPhone} />
                                        <DetailField label="Applicant phone" value={detail.info?.boxTruckOwnerInfo?.applicantPhone} />
                                        <DetailField label="Emergency phone" value={detail.info?.boxTruckOwnerInfo?.emergencyPhone} />
                                        <DetailField
                                            label="Address"
                                            value={
                                                [
                                                    detail.info?.boxTruckOwnerInfo?.address,
                                                    detail.info?.boxTruckOwnerInfo?.city,
                                                    detail.info?.boxTruckOwnerInfo?.state,
                                                    detail.info?.boxTruckOwnerInfo?.zip,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ') || undefined
                                            }
                                        />
                                    </Box>
                                </Box>
                            )}

                            {/* Two-column body: registration on the left, note + documents on the right. */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr 1fr' }, gap: 3, alignItems: 'start', mt: 2 }}>
                                <Box>
                                    <Typography level="title-sm" sx={{ mb: 1 }}>
                                        Contact & registration
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                        <DetailField
                                            label="Email (login)"
                                            value={detail.phoneSignup ? `${detail.email} (phone sign-up — no real email)` : detail.email}
                                        />
                                        <DetailField label="Phone" value={detail.info?.phone || detail.signupPhone} />
                                        <DetailField label="Sign-up phone" value={detail.signupPhone} />
                                        <DetailField label="Company phone" value={detail.info?.companyPhone} />
                                        <DetailField label="Equipment" value={detail.info?.equipmentType} />
                                        <DetailField label="Truck number" value={detail.info?.truckNumber} />
                                        <DetailField label="USDOT" value={detail.info?.dotNumber} />
                                        <DetailField label="MC number" value={detail.info?.mcNumber} />
                                        <DetailField label="Bank" value={detail.info?.bankName} />
                                        <DetailField label="Account number" value={detail.info?.bankAccountNumber} />
                                        <DetailField label="Routing number" value={detail.info?.bankRoutingNumber} />
                                        <DetailField label="Dimensions (L×W×H)" value={formatBoxTruckDimensions(detail.info?.cargoDimensions)} />
                                        <DetailField label="Vehicle make" value={detail.info?.vehicleMake} />
                                        <DetailField label="Vehicle model" value={detail.info?.vehicleModel} />
                                        <DetailField label="Vehicle year" value={detail.info?.vehicleYear} />
                                        <DetailField label="VIN number" value={detail.info?.vinNumber} />
                                        <DetailField label="Door (W×H)" value={formatBoxTruckDoor(detail.info?.doorWidth, detail.info?.doorHeight)} />
                                        <DetailField label="Insurance expiration date" value={detail.info?.insuranceExpirationDate} />
                                        <DetailField label="License number" value={detail.info?.licenseNumber} />
                                        <DetailField label="License state" value={detail.info?.licenseState} />
                                        <DetailField label="License type" value={detail.info?.licenseType} />
                                        <DetailField label="License expiration date" value={detail.info?.licenseExpirationDate} />
                                        <DetailField label="Planned address" value={detail.info?.plannedAddress} />
                                        <DetailField
                                            label="Max cargo weight"
                                            value={detail.info?.maxCargoWeightLbs != null ? `${detail.info.maxCargoWeightLbs.toLocaleString()} lbs` : undefined}
                                        />
                                        <DetailField label="Emergency contact" value={detail.info?.emergencyContact} />
                                        <DetailField label="Emergency phone" value={detail.info?.emergencyContactPhone} />
                                        <DetailField
                                            label="Location"
                                            value={
                                                detail.lastLocation?.city || detail.profileLocation?.city
                                                    ? `${detail.lastLocation?.city || detail.profileLocation?.city}, ${detail.lastLocation?.state || detail.profileLocation?.state || ''}`
                                                    : undefined
                                            }
                                        />
                                        <DetailField label="Submitted" value={fmtDate(detail.submittedAt)} />
                                        <DetailField label="Reviewed" value={fmtDate(detail.reviewedAt)} />
                                        <DetailField
                                            label="Agreed to terms"
                                            value={detail.info?.agreedToTermsAt ? fmtDate(detail.info.agreedToTermsAt) : undefined}
                                        />
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                        Numeo note
                                    </Typography>
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                                        Staff-only — never shown to operators or drivers.
                                    </Typography>
                                    <Textarea
                                        minRows={5}
                                        maxRows={10}
                                        placeholder="Add an internal note about this driver…"
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value)}
                                        slotProps={{ textarea: { maxLength: 2000 } }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <Button
                                            size="sm"
                                            loading={noteSaving}
                                            disabled={noteDraft === (detail.staffNote?.note ?? '')}
                                            onClick={() => void saveNote()}
                                        >
                                            Save note
                                        </Button>
                                        {detail.staffNote && (
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {detail.staffNote.updatedBy || 'staff'} · {fmtDate(detail.staffNote.updatedAt)}
                                            </Typography>
                                        )}
                                    </Box>

                                    {!!detail.info?.documents?.length && (
                                        <>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Typography level="title-sm" sx={{ mb: 1 }}>
                                                Documents ({detail.info.documents.length}
                                                {detail.info.documents.some((d) => d.removedAt)
                                                    ? `, ${detail.info.documents.filter((d) => d.removedAt).length} removed`
                                                    : ''}
                                                )
                                            </Typography>
                                            {/* Grouped by type, admin-style: one tinted section per document kind, its files inside. */}
                                            <Box sx={{ borderRadius: 'md', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                                {docSectionTypes(detail.verificationPath, detail.info?.documents ?? []).map((type, idx) => {
                                                    const files = (detail.info?.documents ?? []).filter((d) => d.type === type)
                                                    const hasActive = files.some((d) => !d.removedAt)
                                                    return (
                                                        <Box
                                                            key={type}
                                                            sx={{
                                                                px: 1.5,
                                                                py: 1,
                                                                bgcolor: hasActive ? 'success.softBg' : files.length ? 'neutral.softBg' : 'warning.softBg',
                                                                borderTop: idx === 0 ? 'none' : '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        >
                                                            <Typography level="body-sm" fontWeight="lg">
                                                                {DOC_TYPE_LABELS[type]}
                                                            </Typography>
                                                            {files.length === 0 ? (
                                                                <Typography level="body-xs" textColor="text.tertiary">
                                                                    Not uploaded
                                                                </Typography>
                                                            ) : (
                                                                <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    {files.map((doc) => (
                                                                        <Box
                                                                            key={doc.gcsPath}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                gap: 1,
                                                                            }}
                                                                        >
                                                                            <Box sx={{ minWidth: 0 }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                                                    <Typography
                                                                                        level="body-sm"
                                                                                        noWrap
                                                                                        sx={
                                                                                            doc.removedAt
                                                                                                ? { textDecoration: 'line-through', opacity: 0.7 }
                                                                                                : undefined
                                                                                        }
                                                                                    >
                                                                                        {doc.fileName}
                                                                                    </Typography>
                                                                                    {doc.removedAt && (
                                                                                        <Chip
                                                                                            size="sm"
                                                                                            variant="soft"
                                                                                            color="danger"
                                                                                            title={doc.removedBy ? `Removed by ${doc.removedBy}` : undefined}
                                                                                        >
                                                                                            Removed
                                                                                        </Chip>
                                                                                    )}
                                                                                </Box>
                                                                                <Typography
                                                                                    level="body-xs"
                                                                                    textColor="text.tertiary"
                                                                                    sx={{ whiteSpace: 'nowrap' }}
                                                                                >
                                                                                    {fmtDate(doc.uploadedAt)}
                                                                                    {doc.removedAt ? ` · removed ${fmtDate(doc.removedAt)}` : ''}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outlined"
                                                                                startDecorator={
                                                                                    docLoading === doc.gcsPath ? (
                                                                                        <CircularProgress size="sm" />
                                                                                    ) : (
                                                                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                                                                    )
                                                                                }
                                                                                disabled={docLoading === doc.gcsPath}
                                                                                onClick={() => void viewDocument(detail.userId, doc)}
                                                                                sx={{ flexShrink: 0 }}
                                                                            >
                                                                                View
                                                                            </Button>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )
                                                })}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                </ModalDialog>
            </Modal>

            {/* Companies breakdown modal — how many drivers each company has claimed. */}
            <Modal open={companiesOpen} onClose={() => setCompaniesOpen(false)}>
                <ModalDialog sx={{ width: 560, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>
                    <ModalClose />
                    <Typography level="h4" startDecorator={<BusinessIcon />}>
                        Drivers by company
                    </Typography>
                    <Table hoverRow sx={{ mt: 1, '--TableCell-paddingX': '10px' }}>
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th style={{ width: 220 }}>Owner</th>
                                <th style={{ width: 90, textAlign: 'right' }}>Drivers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(stats?.companies ?? []).map((company) => (
                                <tr
                                    key={company.applicationId}
                                    onClick={() => drillIntoCompany(company)}
                                    style={{ cursor: 'pointer' }}
                                    title="Show this company's drivers"
                                >
                                    <td>
                                        <Typography level="body-sm" fontWeight="lg">
                                            {companyLabel(company)}
                                        </Typography>
                                        <Typography level="body-xs" textColor="text.tertiary" noWrap>
                                            {company.projectName}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs" noWrap>
                                            {company.ownerEmail || '—'}
                                        </Typography>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Chip size="sm" variant="soft" color="primary">
                                            {company.driverCount}
                                        </Chip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </ModalDialog>
            </Modal>
        </Box>
    )
}
